/**
 * @file apps/web/src/components/AppShell.tsx
 * @description Main application shell — fixed ink sidebar + scrollable content area.
 * Matches veridox.co: Syne logo, nav items with icons, user avatar footer.
 * Redirects to /login if user is not authenticated.
 */

import { Outlet, NavLink, useParams, Link, Navigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  LayoutDashboard, FolderOpen, FlaskConical,
  FileText, CheckCircle, Upload, ChevronRight, BarChart2,
  Lock, Settings, LogOut, Shield, Inbox, Compass, Layers, Users,
} from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { usePortalAuth } from '../lib/portalAuth.js';
import { approvalsApi, projectsApi } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';
import { subscribeProjectOverviewChanged, createLatestOnly } from '../lib/projectOverviewEvents.js';
import { isPhaseComplete, isNavItemLocked } from '../lib/navGates.js';

interface NavItem {
  to:    string;
  label: string;
  icon:  React.ReactNode;
  exact?: boolean;
}

const topNavItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} />, exact: true },
  { to: '/projects',  label: 'Projects',  icon: <FolderOpen size={16} /> },
];

interface PhaseStatus {
  phase:    'discovery' | 'design' | 'build' | 'test' | 'close';
  status:   'locked' | 'in_progress' | 'complete';
}

interface GateFlag {
  unlocked: boolean;
}

interface OverviewData {
  phases:     PhaseStatus[];
  uatGate:    GateFlag;
  launchGate: GateFlag;
}

interface NavPhaseGate {
  gatePhase: PhaseStatus['phase'] | null; // null = never locked
  tooltip:   string;
  // Consults overview's uatGate/launchGate in addition to gatePhase — for
  // the two nav items whose unlock condition is a compound one (an
  // upstream phase AND a workspace_settings-gated approval), which can't
  // be expressed as a single phase's own completion status without
  // polluting that phase's own progress display.
  extraGate?: 'uat' | 'launch';
  // Design and Build reuse Documents'/Discovery's routes as their actionable
  // surface rather than having a page of their own — without this, NavLink's
  // own isActive matching highlights both same-route items simultaneously
  // (e.g. clicking Discovery also highlights Build). Only the canonical,
  // always-accessible entry for a route should ever show as active.
  highlight?: boolean; // default true
}

function ProjectNav({ projectId, devHeaders }: { projectId: string; devHeaders: Record<string, string> }) {
  const base = `/projects/${projectId}`;
  const [overview, setOverview] = useState<OverviewData | null>(null);
  // Which locked item's explanation popover is open, keyed by the same
  // `${to}-${idx}` string used for the item's React key. Auto-dismisses so
  // a stale popover can't linger after the user's attention has moved on.
  const [lockedClickId, setLockedClickId] = useState<string | null>(null);

  // Keep the latest devHeaders reachable from the long-lived loader below
  // without re-subscribing every render.
  const devHeadersRef = useRef(devHeaders);
  devHeadersRef.current = devHeaders;

  // Fetch the overview on mount and again whenever something that feeds a
  // phase gate changes — a page announces that via notifyProjectOverviewChanged
  // (e.g. DiscoveryPage after each disposition save), and returning to the
  // tab refetches too, which covers changes made elsewhere. createLatestOnly
  // makes overlapping refetches safe: only the newest response is applied.
  // A failed REFETCH keeps the last good overview rather than clearing it, so
  // a network blip can't suddenly re-lock the whole nav.
  useEffect(() => {
    const refresh = createLatestOnly(
      () => projectsApi.overview(projectId, devHeadersRef.current),
      (data) => setOverview(data as unknown as OverviewData),
    );
    void refresh();

    const unsubscribe = subscribeProjectOverviewChanged(projectId, () => { void refresh(); });
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [projectId]);

  useEffect(() => {
    if (!lockedClickId) return;
    const timer = setTimeout(() => setLockedClickId(null), 3000);
    return () => clearTimeout(timer);
  }, [lockedClickId]);

  const isComplete = (phase: PhaseStatus['phase']) => isPhaseComplete(overview, phase);

  const uatTooltip = !isComplete('build')
    ? 'Complete Build phase first'
    : overview && !overview.uatGate.unlocked
      ? 'FDD approval required before UAT can begin'
      : 'Complete Build phase first';
  const launchTooltip = !isComplete('test')
    ? 'Complete UAT first'
    : overview && !overview.launchGate.unlocked
      ? 'UAT sign-off approval required before Launch'
      : 'Complete UAT first';

  const items: (NavItem & NavPhaseGate)[] = [
    { to: base,                label: 'Overview',  icon: <LayoutDashboard size={14} />, exact: true, gatePhase: null, tooltip: '' },
    { to: `${base}/discovery`, label: 'Discovery', icon: <Compass size={14} />, gatePhase: null, tooltip: '' },
    { to: `${base}/design`,    label: 'Design',     icon: <FileText size={14} />, gatePhase: 'discovery', tooltip: 'Complete Discovery first' },
    { to: `${base}/build`,     label: 'Build',      icon: <Layers size={14} />, gatePhase: 'design', tooltip: 'Complete Design first' },
    { to: `${base}/uat`,       label: 'UAT',        icon: <FlaskConical size={14} />, gatePhase: 'build', tooltip: uatTooltip, extraGate: 'uat' },
    { to: `${base}/close`,     label: 'Launch',     icon: <Lock size={14} />, gatePhase: 'test', tooltip: launchTooltip, extraGate: 'launch' },
    { to: `${base}/track`,     label: 'Track',      icon: <BarChart2 size={14} />, gatePhase: null, tooltip: '' },
    { to: `${base}/documents`, label: 'Documents',  icon: <FileText size={14} />, gatePhase: null, tooltip: '' },
    { to: `${base}/approvals`, label: 'Approvals',  icon: <CheckCircle size={14} />, gatePhase: null, tooltip: '' },
    { to: `${base}/members`,   label: 'Members',    icon: <Users size={14} />, gatePhase: null, tooltip: '' },
  ];

  return (
    <div className="mt-1 mb-2">
      <p className="px-4 py-1 text-[10px] font-mono font-semibold text-white/30 uppercase tracking-widest">
        Project
      </p>
      {items.map((item, idx) => {
        const locked = isNavItemLocked(overview, item);
        const itemKey = `${item.to}-${idx}`;
        return (
          <div key={itemKey} className="relative">
            <NavLink
              to={item.to}
              end={item.exact}
              title={locked ? item.tooltip : undefined}
              aria-disabled={locked}
              onClick={(e) => {
                if (locked) {
                  // Hard gate: locked items don't navigate. Toggle an inline
                  // popover explaining why instead of silently redirecting.
                  e.preventDefault();
                  setLockedClickId((prev) => (prev === itemKey ? null : itemKey));
                } else {
                  setLockedClickId(null);
                }
              }}
              className={({ isActive }) => {
                const active = isActive && item.highlight !== false;
                return [
                  'flex items-center gap-2.5 pl-6 pr-3 py-1.5 text-[13px] font-medium transition-colors',
                  locked ? 'opacity-50 cursor-not-allowed' : '',
                  active
                    ? 'text-white bg-white/10 border-l-2 border-blue'
                    : 'text-white/55 hover:text-white/90 hover:bg-white/6 border-l-2 border-transparent',
                ].join(' ');
              }}
            >
              <span className="opacity-70">{locked ? <Lock size={14} /> : item.icon}</span>
              {item.label}
            </NavLink>
            {locked && lockedClickId === itemKey && (
              <div className="absolute left-6 right-3 top-full mt-0.5 z-40 px-2.5 py-1.5 rounded bg-ink border border-white/15 shadow-lg text-[11px] text-white/90 leading-snug">
                {item.tooltip}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AppShell() {
  const { projectId } = useParams();
  const { user, isAuthenticated, setUser, devHeaders } = useAuth();
  const { setUser: setPortalUser } = usePortalAuth();
  const [inboxCount, setInboxCount] = useState(0);

  // Roles that can access settings/admin pages
  const canManageSettings = !user.role ||
    user.role === 'owner' || user.role === 'admin' || user.role === 'lead';

  useEffect(() => {
    if (!isAuthenticated) return;
    approvalsApi.inbox(devHeaders)
      .then((data) => setInboxCount((data as unknown[]).length))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const initials = user.fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--gray)' }}>
      {/* ------------------------------------------------------------------ */}
      {/* Sidebar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <aside
        className="w-[220px] shrink-0 flex flex-col fixed top-0 left-0 h-screen z-30"
        style={{ backgroundColor: 'var(--ink)' }}
      >
        {/* Logo */}
        <div className="px-5 py-4 border-b border-white/8 flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-mono font-bold shrink-0"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            V
          </div>
          <span className="font-display text-[17px] font-bold tracking-tight text-white">
            Veridox
          </span>
        </div>

        {/* Top nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          <div className="mb-2">
            {topNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2.5 mx-2 px-3 py-2 rounded text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-white/12 text-white'
                      : 'text-white/60 hover:bg-white/8 hover:text-white',
                  ].join(' ')
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
            {!projectId && (
              <NavLink
                to="/approvals/inbox"
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2.5 mx-2 px-3 py-2 rounded text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-white/12 text-white'
                      : 'text-white/60 hover:bg-white/8 hover:text-white',
                  ].join(' ')
                }
              >
                <Inbox size={16} />
                Approvals
                {inboxCount > 0 && (
                  <span className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue text-white text-[10px] font-mono font-bold">
                    {inboxCount > 99 ? '99+' : inboxCount}
                  </span>
                )}
              </NavLink>
            )}
          </div>

          {/* Project-scoped sub-nav when inside a project */}
          {projectId && <ProjectNav projectId={projectId} devHeaders={devHeaders} />}
        </nav>

        {/* Bottom quick-access links */}
        <div className="px-2 py-2 border-t border-white/8 space-y-0.5">
          <NavLink
            to={projectId ? `/projects/${projectId}/discovery?upload=1` : '/projects'}
            className="flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] font-medium
                       text-white/50 hover:bg-white/8 hover:text-white transition-colors"
          >
            <Upload size={14} />
            Upload Recording
          </NavLink>
          {canManageSettings && (
            <Link
              to="/settings/roles"
              className="flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] font-medium
                         text-white/50 hover:bg-white/8 hover:text-white transition-colors"
            >
              <Shield size={14} />
              Roles
            </Link>
          )}
          {canManageSettings && (
            <Link
              to="/settings"
              className="flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] font-medium
                         text-white/50 hover:bg-white/8 hover:text-white transition-colors"
            >
              <Settings size={14} />
              Settings
            </Link>
          )}
        </div>

        {/* User footer */}
        <div className="px-3 py-3 border-t border-white/8 flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-mono font-bold shrink-0"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium text-white truncate">{user.fullName}</p>
            <p className="text-[10px] text-white/40 font-mono truncate">{user.email}</p>
          </div>
          <button
            onClick={() => { void supabase.auth.signOut(); setUser(null); setPortalUser(null); }}
            className="btn-icon text-white/40 hover:text-white ml-1 shrink-0"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </aside>

      {/* ------------------------------------------------------------------ */}
      {/* Content area (offset by sidebar width)                             */}
      {/* ------------------------------------------------------------------ */}
      <main className="flex-1 ml-[220px] min-h-screen overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

export { ChevronRight };
